import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Typography,
    SelectChangeEvent,
    Button,
    InputLabel,
} from '@mui/material';
import { isRecordFound, useRefreshLocationList, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { FiltersData, ManageAlarmsTabsEnum, isDataLoaded } from '../../types';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { useSiteListData } from '../../data/DataProviders/SiteListProvider';
import { useDispatch, useSelector } from 'react-redux';
import { Actions, Thunks } from '../../actions';
import { getUserId, useAuthHelper, useAuthState } from '../../components/AuthContextProvider';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { BLG300, BLG400, BLUEPRINT_ORGANIZATION_NAME } from '../../constants';
import { Selectors } from '../../selectors';
import { useGatewayDeviceList } from '../../data/DataProviders/GatewayDeviceListProvider';
import { useAlarmFilterData } from '../../data/DataProviders/AlarmFilterProvider';
import { sortList } from '../../lib/manage-alarms-helpers';
import { MasterDataKey } from '../../types/device';

export const AlarmsFilters: React.FC<{ value: any; deviceChannelData: Function }> = (props) => {
    const dispatch = useDispatch();
    const ITEM_HEIGHT = 48;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5,
                width: 200,
            },
        },
    };
    const GateWayMenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5,
                width: 250,
            },
        },
    };
    const [sitesList, setSitesList] = useState<any[]>([]);
    const [gatewayTypeList, setGatewayTypeList] = useState<any[]>([]);
    const [locationList, setLocationList] = useState<any[]>([]);
    const [gatewayList, setGatewayList] = useState<any[]>([]);

    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const userId = getUserId(useAuthState());
    const locationListData = useRefreshLocationList(); //this hook used when location need to refresg after create or delete
    const alarmFilterData = useAlarmFilterData();

    const deviceMasterData = useDeviceMaster();
    const siteListData = useSiteListData();
    const gatewayListData = useGatewayDeviceList();

    const [defaultLocation, setDefaultLocation] = useState<number>(0);
    const [defaultSite, setDefaultSite] = useState<number>(0);
    const [defaultGatewayType, setDefaultGatewayType] = useState<number>(0);
    const [defaultDeviceType, setDefaultDeviceType] = useState<number>(0);
    const [defaultGateway, setDefaultGateway] = useState<number>(0);
    const [deviceProfiles, setDeviceProfiles] = useState<any[]>([]);

    const selectedOrganization = useSelectedOrganizationData();
    const orgName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';
    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
    const manageAlarmState = useSelector(Selectors.ManageAlarmsSelectors.filtersData);
    const [filtersData, setFiltersData] = useState<FiltersData>({
        siteId: '',
        locationId: '',
        deviceType: '',
        gatewayTypeId: '',
        gatewayId: '',
        alarmType: '',
        type: ManageAlarmsTabsEnum.singlealarms,
    });

    useEffect(() => {
        if (manageAlarmState.success) {
            alarmFilterData.getAlarmFilterData(orgId, userId);
            dispatch(Actions.ManageAlarmsActions.AlarmFiltersSaveUnmounted());
            dispatch(Actions.ManageAlarmsActions.AlarmFiltersUpdateUnmounted());
        }
    }, [manageAlarmState.success]);

    useEffect(() => {
        if (props) {
            const filtersDataObj: FiltersData = { ...manageAlarmState.filters };
            filtersDataObj['type'] =
                props.value === 0 ? ManageAlarmsTabsEnum.singlealarms : ManageAlarmsTabsEnum.bulkalarms;
            setFiltersData(filtersDataObj);
        }
    }, [props.value]);

    useEffect(() => {
        if (isDataLoaded(locationListData)) {
            setLocationList(locationListData.value.locations);
        }
    }, [locationListData]);

    useEffect(() => {
        if (locationList.length > 0 && isDataLoaded(alarmFilterData.alarmFilter)) {
            sortList(locationList, 'locationName');
            const filtersDataObj: any = { ...manageAlarmState.filters };
            if (manageAlarmState.filters.locationId && manageAlarmState.filters.locationId !== '') {
                const index = locationList.findIndex((item) => item.locationId === manageAlarmState.filters.locationId);
                if (index !== -1) {
                    filtersDataObj['locationId'] = manageAlarmState.filters.locationId;
                    setDefaultLocation(index);
                } else {
                    filtersDataObj['locationId'] = locationList[0].locationId;
                    setDefaultLocation(0);
                }
            } else {
                filtersDataObj['locationId'] = locationList[0].locationId;
                dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
                setDefaultLocation(0);
            }
            setFiltersData(filtersDataObj);
        }
    }, [locationList, alarmFilterData]);

    useEffect(() => {
        if (isDataLoaded(siteListData) && siteListData.value.length > 0) {
            setSitesList(siteListData.value);
        }
    }, [siteListData]);

    useEffect(() => {
        const filtersDataObj: FiltersData = { ...manageAlarmState.filters };
        if (sitesList.length > 0) {
            sortList(sitesList, 'siteName');
            if (manageAlarmState.filters.siteId && manageAlarmState.filters.siteId !== '') {
                const index = sitesList.findIndex((item) => item.siteId === manageAlarmState.filters.siteId);
                if (index !== -1) {
                    filtersDataObj['siteId'] = manageAlarmState.filters.siteId;
                    setDefaultSite(index);
                } else {
                    filtersDataObj['siteId'] = sitesList[0].siteId;
                    setDefaultSite(0);
                }
            } else {
                filtersDataObj['siteId'] = sitesList[0].siteId;
                setDefaultSite(0);
            }
        } else {
            filtersDataObj['siteId'] = '';
        }
        setFiltersData(filtersDataObj);
        dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
    }, [sitesList]);

    useEffect(() => {
        if (isDataLoaded(deviceMasterData.deviceTypeData)) {
            const starfishGateway = deviceMasterData.deviceTypeData.value.filter((item: any) => item.key.toLowerCase() === BLG300 || item.key.toLowerCase() === BLG400);
            setGatewayTypeList(starfishGateway);
        }
    }, [deviceMasterData]);

    useEffect(() => {
        if (gatewayTypeList.length > 0) {
            const filtersDataObj: FiltersData = { ...manageAlarmState.filters };
            sortList(gatewayTypeList, 'value');
            if (manageAlarmState.filters.gatewayTypeId && manageAlarmState.filters.gatewayTypeId !== '') {
                const index = gatewayTypeList.findIndex(
                    (item) => item.gatewayProfileId === manageAlarmState.filters.gatewayTypeId
                );
                if (index !== -1) {
                    filtersDataObj['gatewayTypeId'] = manageAlarmState.filters.gatewayTypeId;
                    setDefaultGatewayType(index);
                } else {
                    filtersDataObj['gatewayTypeId'] = gatewayTypeList[gatewayTypeList.length - 1].gatewayProfileId;
                    setDefaultGatewayType(0);
                    dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
                }
            } else {
                const blgIndex = gatewayTypeList.findIndex((item) => item.key.toLowerCase() === BLG300 || item.key.toLowerCase() === BLG400);
                filtersDataObj['gatewayTypeId'] = gatewayTypeList[blgIndex].gatewayProfileId;
                setDefaultGatewayType(blgIndex);
                dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
            }
            setFiltersData(filtersDataObj);
        }
    }, [gatewayTypeList]);

    useEffect(() => {
        if (isDataLoaded(gatewayListData)) {
            setGatewayList(gatewayListData.value);
        }
    }, [gatewayListData]);

    useEffect(() => {
        const filtersDataObj: FiltersData = { ...manageAlarmState.filters };
        if (gatewayList.length > 0) {
            sortList(gatewayList, 'gatewayName');

            if (manageAlarmState.filters.gatewayId && manageAlarmState.filters.gatewayId !== '') {
                const index = gatewayList.findIndex((item) => item.gatewayId === manageAlarmState.filters.gatewayId);
                if (index !== -1) {
                    filtersDataObj['gatewayId'] = manageAlarmState.filters.gatewayId;
                    setDefaultGateway(index);
                } else {
                    filtersDataObj['gatewayId'] = gatewayList[0].gatewayId;
                    setDefaultGateway(0);
                }
            } else {
                filtersDataObj['gatewayId'] = gatewayList[0].gatewayId;
                setDefaultGateway(0);
            }
        } else {
            filtersDataObj['gatewayId'] = '';
        }
        dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
        setFiltersData(filtersDataObj);
    }, [gatewayList]);

    useEffect(() => {
        deviceMasterData.getDeviceTypeMasterData(orgName, 'gateway');
    }, []);

    useEffect(() => {
        deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, MasterDataKey.Starfish);
    }, []);

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            deviceMasterData.rowKey === MasterDataKey.Starfish
        ) {
            const deviceType = deviceMasterData.deviceInfo.value;
            deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, orgId);
            if (deviceType) {
                setDeviceProfiles(deviceType);
            }
        }
    }, [deviceMasterData]);

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            deviceMasterData.rowKey === orgId
        ) {
            const childDeviceInfo = deviceMasterData.deviceInfo.value;
            if (childDeviceInfo) {
                const orgProfiles = JSON.parse(JSON.stringify(childDeviceInfo));
                const allDeviceProfiles = orgProfiles.concat(deviceProfiles);
                sortList(allDeviceProfiles, 'key');
                setDeviceProfiles(allDeviceProfiles);
            }
        }
    }, [deviceMasterData.deviceInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (deviceProfiles.length > 0) {
            sortList(deviceProfiles, 'key');
            const filtersDataObj: FiltersData = { ...manageAlarmState.filters };
            if (manageAlarmState.filters.deviceType && manageAlarmState.filters.deviceType !== '') {
                const index = deviceProfiles.findIndex(
                    (item) => item.value.profileName === manageAlarmState.filters.deviceType
                );
                if (index !== -1) {
                    filtersDataObj['deviceType'] = manageAlarmState.filters.deviceType;
                    setDefaultDeviceType(index);
                } else {
                    filtersDataObj['deviceType'] = deviceProfiles[0].value.profileName;
                    setDefaultDeviceType(0);
                }
            } else {
                const defaultDeviceTypeIndex = deviceProfiles.findIndex((item) => !item.value.disabled);
                filtersDataObj['deviceType'] = deviceProfiles[defaultDeviceTypeIndex].value.profileName;
                setDefaultDeviceType(defaultDeviceTypeIndex);
            }
            dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
            setFiltersData(filtersDataObj);
        }
    }, [deviceProfiles]);

    const handleFilterChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
        type: string
    ): void => {
        const filtersDataObj: any = { ...manageAlarmState.filters };
        filtersDataObj[type] = event.target.value;
        setFiltersData(filtersDataObj);
        dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersDataObj));
    };

    useEffect(() => {
        if (!manageAlarmState.configLoading && manageAlarmState.configSuccess) {
            dispatch(Actions.ManageAlarmsActions.AlarmConfigSaveUnmounted());
            addToast('Alarm Configuration Saved');
        }
    }, [manageAlarmState.configSuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    const onApplyFilters = (): void => {
        if (isDataLoaded(alarmFilterData.alarmFilter)) {
            dispatch(Actions.ManageAlarmsActions.SetAlarmFilters(filtersData));
            if (
                alarmFilterData.alarmFilter.value.contextualDataId &&
                alarmFilterData.alarmFilter.value.contextualDataId !== ''
            ) {
                dispatch(
                    Thunks.ManageAlarmsThunks.UpdateAlarmFilters(
                        filtersData,
                        alarmFilterData.alarmFilter.value.contextualDataId,
                        userId || '',
                        authHelper,
                        addToast
                    )
                );
            } else
                dispatch(
                    Thunks.ManageAlarmsThunks.SaveAlarmFilters(filtersData, orgId, userId || '', authHelper, addToast)
                );
            props.deviceChannelData(filtersData);
        }
    };

    const isDisable = (): boolean => {
        let isApplyDisable = false;
        if (filtersData)
            if (
                !filtersData.locationId ||
                filtersData.locationId === '' ||
                !filtersData.siteId ||
                filtersData.siteId === '' ||
                !filtersData.gatewayTypeId ||
                filtersData.gatewayTypeId === '' ||
                !filtersData.gatewayId ||
                filtersData.gatewayId === '' ||
                !filtersData.deviceType ||
                filtersData.deviceType === ''
            ) {
                isApplyDisable = true;
            }
        return isApplyDisable;
    };

    return (
        <>
            <Paper style={{ padding: 10, display: 'flex', width: '100%' }}>
                <Typography ml={2} mt={2} color="secondary" style={{ flex: '0 0 auto' }}>
                    Filter By:
                </Typography>

                <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {isDataLoaded(locationListData) && locationList.length > 0 && (
                        <Box style={{ paddingLeft: 0 }}>
                            <FormControl required sx={{ m: 1, width: 250 }} size="small">
                                <InputLabel id="location-select-label">Location</InputLabel>
                                <Select
                                    label="Location"
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    defaultValue={locationList[defaultLocation]}
                                    onChange={(e): void => {
                                        handleFilterChange(e, 'locationId');
                                    }}
                                    value={filtersData.locationId}
                                    MenuProps={MenuProps}
                                >
                                    {locationList.map(
                                        (data: { locationId: string; locationName: string }, index: number) => (
                                            <MenuItem
                                                key={index}
                                                value={data.locationId}
                                                data-testid={`locationList${index}`}
                                            >
                                                {data.locationName}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <Box style={{ paddingLeft: 0 }}>
                        <FormControl required sx={{ m: 1, width: 250 }} size="small">
                            <InputLabel id="site-select-label">Site</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Site"
                                defaultValue={sitesList[defaultSite]}
                                onChange={(e): void => {
                                    handleFilterChange(e, 'siteId');
                                }}
                                value={filtersData.siteId}
                                MenuProps={MenuProps}
                            >
                                {sitesList.map((siteData: { siteId: string; siteName: string }, index: number) => (
                                    <MenuItem key={index} value={siteData.siteId} data-testid={`site-menu${index}`}>
                                        {siteData.siteName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box style={{ paddingLeft: 0 }}>
                        <FormControl required sx={{ m: 1, width: 300 }} size="small">
                            <InputLabel id="gateway-select-label">Gateway Type</InputLabel>
                            <Select
                                inputProps={{ 'aria-label': 'Without label' }}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Gateway Type"
                                disabled={false}
                                MenuProps={GateWayMenuProps}
                                defaultValue={gatewayTypeList[defaultGatewayType]}
                                onChange={(e): void => {
                                    handleFilterChange(e, 'gatewayTypeId');
                                }}
                                value={filtersData.gatewayTypeId}
                            >
                                {gatewayTypeList.map(
                                    (gateway: { gatewayProfileId: string; value: string }, index: number) => (
                                        <MenuItem
                                            key={index}
                                            value={gateway.gatewayProfileId}
                                            data-testid={`gateway-item${index}`}
                                        >
                                            {gateway.value}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box style={{ paddingLeft: 0 }}>
                        <FormControl required sx={{ m: 1, width: 250 }} size="small">
                            <InputLabel id="gateway-select-label">Gateway Id</InputLabel>
                            <Select
                                inputProps={{ 'aria-label': 'Without label' }}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Gateway Id"
                                MenuProps={GateWayMenuProps}
                                defaultValue={gatewayList[defaultGateway]}
                                onChange={(e): void => {
                                    handleFilterChange(e, 'gatewayId');
                                }}
                                value={filtersData.gatewayId}
                            >
                                {gatewayList.map(
                                    (gateway: { gatewayId: string; gatewayName: string }, index: number) => (
                                        <MenuItem
                                            key={index}
                                            value={gateway.gatewayId}
                                            data-testid={`gateway-${index}`}
                                        >
                                            {gateway.gatewayName}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box style={{ paddingLeft: 0 }}>
                        <FormControl required sx={{ m: 1, width: 250 }} size="small">
                            <InputLabel id="gateway-select-label">Device Type</InputLabel>

                            <Select
                                inputProps={{ 'aria-label': 'Without label' }}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Device Type"
                                MenuProps={MenuProps}
                                defaultValue={deviceProfiles[defaultDeviceType]}
                                onChange={(e): void => {
                                    handleFilterChange(e, 'deviceType');
                                }}
                                value={filtersData.deviceType}
                            >
                                {deviceProfiles.map(
                                    (
                                        data: {
                                            value: { profileName: string; displayName: string; disabled: boolean };
                                        },
                                        index: number
                                    ) => (
                                        <MenuItem
                                            key={index}
                                            disabled={data.value.disabled}
                                            value={data.value.profileName}
                                            data-testid={`deviceType${index}`}
                                        >
                                            {data.value.displayName}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box style={{ padding: 30, flex: '0 0 auto' }}>
                    {' '}
                    <Button
                        id="btnLogin"
                        fullWidth
                        style={{ marginLeft: 10 }}
                        variant="contained"
                        color="primary"
                        disabled={isDisable()}
                        onClick={onApplyFilters}
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Paper>
            {/* {!isDataLoaded(gatewayListData) && (
                <DataIndicators
                    errorMessage="Alarm Filters not Found"
                    emptyMessage="alarmFiltersData"
                    inline
                    data={gatewayListData}
                    emptyIcon="Devices"
                    isEmpty={false}
                    isDetailedInfo={false}
                />
            )} */}
        </>
    );
};
